import { stringAttr } from "@wiki/core";
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { EditorState } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { linkRange, removeLink, setLink, setLinkOver } from "../commands/marks";
import type { EditorContext } from "../context";
import { closeLinkToolbar, linkToolbarKey } from "../plugins/link-toolbar";
import type { MentionSearchResult } from "../types";
import { MenuPopover, ToolbarButton, placementAtPos } from "./MenuPopover";

export interface LinkToolbarProps {
  view: EditorView;
  state: EditorState;
  context: EditorContext;
}

interface Draft {
  /** The link the draft belongs to, so a new link starts from its own href. */
  anchor: number;
  value: string;
}

/**
 * The link toolbar of `editor.md` §4: an editable href field that doubles as a
 * document search, Open, and Remove. `Mod-K` creates the mark first and lands
 * the caret here, which is why the field takes focus when the plugin says the
 * toolbar is in edit mode.
 */
export function LinkToolbar({ view, state, context }: LinkToolbarProps): ReactNode {
  const range = linkRange(state);
  const editing = linkToolbarKey.getState(state)?.editing === true;
  const href = range ? (stringAttr(range.mark, "href") ?? "") : "";
  const anchor = range?.from ?? -1;
  const [draft, setDraft] = useState<Draft>({ anchor: -1, value: "" });
  const [results, setResults] = useState<{ query: string | null; found: MentionSearchResult[] }>({
    query: null,
    found: [],
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const value = draft.anchor === anchor ? draft.value : href;

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  useEffect(() => {
    const search = context.searchMentions;
    // A query that is not a URL searches documents, so an internal link can be
    // picked from results rather than typed.
    if (!editing || !search || value === "" || /^(https?:)?\/\//.test(value)) return;
    let cancelled = false;
    void search(value, "document")
      .then((found) => {
        if (!cancelled) setResults({ query: value, found: found.slice(0, 5) });
      })
      .catch(() => {
        if (!cancelled) setResults({ query: value, found: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [context.searchMentions, editing, value]);

  if (!range || context.readOnly) return null;
  const suggestions = results.query === value ? results.found : [];

  const apply = (next: string): void => {
    // The range, not the selection: the caret sits inside the link it is
    // editing, and the whole link is what takes the new href (§4).
    const command = range ? setLinkOver(range.from, range.to, next) : setLink(next);
    command(view.state, view.dispatch, view);
    closeLinkToolbar(view);
    view.focus();
  };

  return (
    <MenuPopover
      placement={placementAtPos(view, range.from)}
      role="dialog"
      label="Link"
      testId="link-toolbar"
      onDismiss={() => {
        closeLinkToolbar(view);
      }}
    >
      <div className="wiki-menu__row">
        <input
          ref={inputRef}
          className="wiki-menu__input"
          aria-label="Link address"
          placeholder="Paste a link or search documents"
          value={value}
          onChange={(event) => {
            setDraft({ anchor, value: event.target.value });
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              apply(value);
            }
          }}
        />
        <ToolbarButton
          label="Open link"
          onSelect={() => {
            context.openLink(href === "" ? value : href);
          }}
        />
        <ToolbarButton
          label="Remove link"
          onSelect={() => {
            removeLink(view.state, view.dispatch, view);
            closeLinkToolbar(view);
            view.focus();
          }}
        />
      </div>
      {suggestions.length > 0 && (
        <ul className="wiki-menu__list" aria-label="Documents">
          {suggestions.map((result) => (
            <li key={result.modelId}>
              <button
                type="button"
                className="wiki-menu__item"
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                onClick={() => {
                  apply(result.href ?? `/doc/${result.modelId}`);
                }}
              >
                {result.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </MenuPopover>
  );
}
